import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

// 'approval' is aliased to the automation field set server-side (UI-2e §0.H).
export type RuleDomain = 'routing' | 'automation' | 'escalation' | 'approval'

export interface FieldDef {
  key: string
  label: string
  type: 'string' | 'number' | 'boolean' | 'enum' | 'array'
  enumValues?: string[]
}

export interface UseRuleFieldsReturn {
  fields: FieldDef[]
  loading: boolean
  error: string | null
}

// Module-level stable fallback so consumers don't see fresh [] each render.
const EMPTY_FIELDS: FieldDef[] = []
const STALE_MS = 5 * 60 * 1000
const cache = new Map<RuleDomain, { items: FieldDef[]; fetchedAt: number }>()

/**
 * Fetches the rule field catalog for a domain from
 * GET /admin/conversations/rule-fields?domain={domain}. Caches per
 * domain at module level so RuleBuilder's field picker doesn't refetch.
 */
export function useRuleFields(domain: RuleDomain): UseRuleFieldsReturn {
  const cached = cache.get(domain)
  const [fields, setFields] = useState<FieldDef[]>(cached?.items ?? EMPTY_FIELDS)
  const [loading, setLoading] = useState(cached === undefined)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<{ fields: FieldDef[] }>(
        `/admin/conversations/rule-fields?domain=${domain}`,
      )
      const items = resp.fields ?? EMPTY_FIELDS
      cache.set(domain, { items, fetchedAt: Date.now() })
      setFields(items)
    } catch (e) {
      setError((e as Error).message)
      setFields(EMPTY_FIELDS)
    } finally {
      setLoading(false)
    }
  }, [domain])

  useEffect(() => {
    const c = cache.get(domain)
    if (c !== undefined && Date.now() - c.fetchedAt <= STALE_MS) {
      setFields(c.items)
      setLoading(false)
      return
    }
    void load()
  }, [domain, load])

  return { fields, loading, error }
}

/** Test-only: clears the module-level cache. */
export function __resetRuleFieldsCache(): void {
  cache.clear()
}
